import React, { useEffect, useState, useCallback } from "react"
import API from "../../api/axios"
import AdminLayout from "./AdminLayout"

const EMPTY = {
  title: "", description: "", instructor: "Mr. Ali",
  sessionDate: "", duration: 60, price: 0, currency: "KES",
  isFree: false, maxParticipants: 50,
  meetingUrl: "", meetingId: "", meetingPassword: "",
  platform: "zoom", status: "upcoming",
  thumbnail: "", isPublished: false, isFeatured: false, recordingUrl: "",
}

const statusColor = { upcoming: "badge-read", live: "badge-active", completed: "badge-replied", cancelled: "badge-new" }
const platformIcon = { zoom: "fa-video", google_meet: "fa-globe", microsoft_teams: "fa-globe", other: "fa-link" }

const AdminLiveSessions = () => {
  const [sessions,  setSessions]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState("")
  const [success,   setSuccess]   = useState("")
  const [showModal, setShowModal] = useState(false)
  const [editing,   setEditing]   = useState(null)
  const [form,      setForm]      = useState(EMPTY)
  const [saving,    setSaving]    = useState(false)
  const [bookingsModal, setBookingsModal] = useState(null) // sessionId
  const [bookings,  setBookings]  = useState([])

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await API.get("/live-sessions/admin/all")
      setSessions(data.data || [])
    } catch { setError("Failed to load sessions.") }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchSessions() }, [fetchSessions])

  const openAdd  = () => { setForm(EMPTY); setEditing(null); setShowModal(true) }
  const openEdit = (s) => {
    setForm({
      ...s,
      sessionDate: s.sessionDate ? new Date(s.sessionDate).toISOString().slice(0,16) : "",
    })
    setEditing(s); setShowModal(true)
  }
  const closeModal = () => { setShowModal(false); setEditing(null) }

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setError(""); setSuccess("")
    try {
      if (editing) { await API.put(`/live-sessions/${editing.id}`, form); setSuccess("Session updated.") }
      else         { await API.post("/live-sessions", form); setSuccess("Session created.") }
      closeModal(); fetchSessions()
    } catch (err) { setError(err.response?.data?.message || "Save failed.") }
    finally { setSaving(false) }
  }

  const del = async (id) => {
    if (!window.confirm("Delete this session?")) return
    try { await API.delete(`/live-sessions/${id}`); fetchSessions() }
    catch { setError("Delete failed.") }
  }

  const viewBookings = async (sessionId) => {
    try {
      const { data } = await API.get(`/live-sessions/${sessionId}/bookings`)
      setBookings(data.data || [])
      setBookingsModal(sessionId)
    } catch { setError("Failed to load bookings.") }
  }

  const approvePayment = async (paymentId) => {
    try {
      await API.patch(`/live-sessions/payments/${paymentId}/approve`)
      setSuccess("Payment approved and session booked.")
    } catch (err) { setError(err.response?.data?.message || "Approval failed.") }
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" }) : "—"
  const f = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value })

  return (
    <AdminLayout title="Live Sessions">
      {error   && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      <div className="admin-section">
        <div className="admin-section-header">
          <h3>All Live Sessions ({sessions.length})</h3>
          <button className="btn-primary" onClick={openAdd}>
            <i className="fa fa-plus"></i> Schedule Session
          </button>
        </div>

        {loading ? (
          <div className="admin-empty"><i className="fa fa-spinner fa-spin"></i> Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="admin-empty">No sessions yet. Schedule your first live session.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date & Time</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th>Platform</th>
                  <th>Booked</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{s.title}</div>
                      <small style={{ color: "#999" }}>{s.instructor}</small>
                    </td>
                    <td style={{ fontSize: 13 }}>{fmtDate(s.sessionDate)}</td>
                    <td style={{ fontSize: 13 }}>{s.duration} min</td>
                    <td style={{ fontWeight: 600 }}>
                      {s.isFree ? <span style={{ color: "#2e7d52" }}>FREE</span> : `KES ${Number(s.price).toLocaleString()}`}
                    </td>
                    <td>
                      <span className="badge badge-read">
                        <i className={`fa ${platformIcon[s.platform] || "fa-video"}`} style={{ marginRight: 4 }}></i>
                        {s.platform?.replace("_"," ")}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>{s.bookedCount || 0} / {s.maxParticipants}</td>
                    <td>
                      <span className={`badge ${statusColor[s.status] || "badge-read"}`}>{s.status}</span>
                      {s.isPublished ? <span className="badge badge-active" style={{ marginLeft: 4 }}>Live</span> : <span className="badge badge-read" style={{ marginLeft: 4 }}>Draft</span>}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <button className="btn-sm btn-edit"    onClick={() => openEdit(s)}>Edit</button>
                        <button className="btn-sm btn-approve" onClick={() => viewBookings(s.id)}>Bookings</button>
                        <button className="btn-sm btn-danger"  onClick={() => del(s.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Session Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editing ? "Edit Session" : "Schedule New Session"}</h3>
              <button className="admin-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={save}>
              <div className="admin-modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>

                <div className="form-group">
                  <label>Title *</label>
                  <input value={form.title} onChange={f("title")} required />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea value={form.description} onChange={f("description")} rows={3} />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Instructor</label>
                    <input value={form.instructor} onChange={f("instructor")} />
                  </div>
                  <div className="form-group">
                    <label>Session Date & Time *</label>
                    <input type="datetime-local" value={form.sessionDate} onChange={f("sessionDate")} required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Duration (minutes)</label>
                    <input type="number" min="15" value={form.duration} onChange={f("duration")} />
                  </div>
                  <div className="form-group">
                    <label>Max Participants</label>
                    <input type="number" min="1" value={form.maxParticipants} onChange={f("maxParticipants")} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Price (KES)</label>
                    <input type="number" min="0" step="0.01" value={form.price} onChange={f("price")} />
                  </div>
                  <div className="form-group">
                    <label>Platform</label>
                    <select value={form.platform} onChange={f("platform")}>
                      <option value="zoom">Zoom</option>
                      <option value="google_meet">Google Meet</option>
                      <option value="microsoft_teams">Microsoft Teams</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Meeting URL (hidden until booked)</label>
                  <input value={form.meetingUrl} onChange={f("meetingUrl")} placeholder="https://zoom.us/j/..." />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Meeting ID</label>
                    <input value={form.meetingId} onChange={f("meetingId")} placeholder="123 456 7890" />
                  </div>
                  <div className="form-group">
                    <label>Meeting Password</label>
                    <input value={form.meetingPassword} onChange={f("meetingPassword")} placeholder="abc123" />
                  </div>
                </div>

                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input value={form.thumbnail} onChange={f("thumbnail")} placeholder="https://..." />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={f("status")}>
                      <option value="upcoming">Upcoming</option>
                      <option value="live">Live Now</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Recording URL (post-session)</label>
                    <input value={form.recordingUrl} onChange={f("recordingUrl")} placeholder="https://..." />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 8 }}>
                  <label className="form-toggle">
                    <input type="checkbox" checked={form.isFree} onChange={f("isFree")} /> Free Session
                  </label>
                  <label className="form-toggle">
                    <input type="checkbox" checked={form.isPublished} onChange={f("isPublished")} /> Published
                  </label>
                  <label className="form-toggle">
                    <input type="checkbox" checked={form.isFeatured} onChange={f("isFeatured")} /> Featured
                  </label>
                </div>
              </div>

              <div className="admin-modal-footer">
                <button type="button" className="btn-sm btn-toggle" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving…</> : "Save Session"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bookings Modal */}
      {bookingsModal && (
        <div className="admin-modal-overlay" onClick={() => setBookingsModal(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Session Bookings</h3>
              <button className="admin-modal-close" onClick={() => setBookingsModal(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              {bookings.length === 0 ? (
                <div className="admin-empty">No bookings yet.</div>
              ) : (
                <table className="admin-table">
                  <thead><tr><th>Student</th><th>Email</th><th>Phone</th><th>Status</th><th>Booked At</th></tr></thead>
                  <tbody>
                    {bookings.map(b => (
                      <tr key={b.id}>
                        <td>{b.user?.name}</td>
                        <td>{b.user?.email}</td>
                        <td>{b.user?.phone || "—"}</td>
                        <td><span className={`badge badge-${b.status === "confirmed" ? "active" : "new"}`}>{b.status}</span></td>
                        <td style={{ fontSize: 12 }}>{fmtDate(b.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminLiveSessions
